import fetchFirstAmounts from "utils/orderAmounts";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/**
 * How much the customer orders under each supplier's own code come to.
 *
 * A supplier IS a shop code - adding an order creates a supplier of the same
 * code - so the orders whose shop_code matches are that supplier's work:
 *
 *   Balance          SUM(final_amount) - everything paid to them
 *   Expected balance SUM(first_amount - the order's own deposit)
 *
 * The expected balance is the sum of each order's expected balance, which is
 * why the deposit is totalled per order here rather than taken from the
 * supplier's account.
 *
 * The server computes both and sends them with the supplier, in which case
 * this does nothing at all.
 *
 * Otherwise both are worked out here. `final_amount` comes off the orders
 * list, but `first_amount` is not on it, so the orders that actually belong
 * to a supplier are read individually to get it - only those, and only up to
 * DETAIL_CAP of them.
 */
export default async function withOrderTotals(suppliers) {
  if (!Array.isArray(suppliers) || suppliers.length === 0) return suppliers;

  // already provided - nothing to work out
  if (suppliers[0].orders_balance !== undefined) return suppliers;

  try {
    const res = await fetch(`${API}/orders?limit=2000`, { cache: "no-store" });
    if (!res.ok) return suppliers;

    const orders = await res.json();
    if (!Array.isArray(orders)) return suppliers;

    const codes = new Set(suppliers.map((s) => String(s.code || "").toUpperCase()).filter(Boolean));

    const mine = orders.filter((o) => codes.has(String(o.shop_code || "").toUpperCase()));

    // only the orders that actually belong to a supplier need this
    const firstById = await fetchFirstAmounts(mine);

    const totals = new Map();
    // whether first_amount could be established at all, per code
    const firstKnown = new Map();

    mine.forEach((o) => {
      const key = String(o.shop_code || "").toUpperCase();

      const t = totals.get(key) || { first: 0, final: 0, deposit: 0 };
      t.final += Number(o.final_amount ?? o.charged) || 0;
      t.deposit += Number(o.deposit) || 0;

      // the inline value first: an order with no id is not in the map
      const first =
        o.first_amount !== undefined && o.first_amount !== null
          ? Number(o.first_amount)
          : firstById.get(o.id);

      if (first !== undefined && first !== null && !Number.isNaN(first)) {
        t.first += first;
        firstKnown.set(key, true);
      }

      totals.set(key, t);
    });

    return suppliers.map((s) => {
      const key = String(s.code || "").toUpperCase();
      const t = totals.get(key) || { first: 0, final: 0, deposit: 0 };
      const paid = Number(s.paid) || 0;

      // a supplier with no orders has an expected balance too - it is simply
      // what has been paid out against nothing
      const known = firstKnown.get(key) || !totals.has(key);

      return {
        ...s,
        orders_first: t.first,
        orders_final: t.final,
        orders_balance: Math.round((t.final - paid) * 100) / 100,
        // null, not 0, when first_amount could not be established: zero would
        // read as "nothing expected" rather than "not known"
        orders_expected_balance: known ? Math.round((t.first - t.deposit) * 100) / 100 : null,
      };
    });
  } catch (e) {
    return suppliers;
  }
}
